import { facturaGradient } from "../../facturasStyles";
import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoImage from "../../../../../assets/images/logo horinzontal.png";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  success: "#27ae60",
  warning: "#ed6c02",
  error: "#d32f2f",
  info: "#2980b9",
  white: "#ffffff",
  gray: "#f5f5f5",
  darkGray: "#333333",
  border: "#e2e8f0",
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "85%", md: "75%" },
  maxWidth: 900,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  overflow: "auto",
  p: { xs: 2, sm: 3, md: 4 },
};

const PaymentReceiptModal = ({ open, onClose, invoiceData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Función para obtener la información formateada para el PDF
  const getReceiptData = (data) => {
    if (!data) return null;

    return {
      facturaNumero: data.invoiceNumber || data.invoice_number || "No disponible",
      empresa: data.company || "No disponible",
      nit: data.nit || "No disponible",
      monto: typeof data.amount === "number" ? data.amount : 0,
      fecha: data.createdAtFormatted || new Date().toLocaleString("es-CO"),
      transactionId: data.paymentId || data.id?.toString() || "No disponible",
      estado: data.statusBadge?.text || "Aprobado",
      statusDetail: data.statusDetail || null,
    };
  };

  // Función para formatear moneda
  const formatCOP = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  // Función para convertir imagen a Base64
  const getLogoBase64 = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      };
      img.onerror = () => {
        console.warn("Error cargando logo, usando texto alternativo");
        resolve(null);
      };
      img.src = logoImage;
    });
  };

  // Función para generar y descargar el PDF
  const handleDownloadPDF = async () => {
    if (!invoiceData) {
      setError("No hay datos de la factura para generar el PDF");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const doc = new jsPDF();
      const receiptData = getReceiptData(invoiceData);
      const logoBase64 = await getLogoBase64();

      if (!receiptData) {
        throw new Error("No se pudieron procesar los datos de la factura");
      }

      // Configuración inicial
      doc.setFont("helvetica");

      // Logo centrado
      if (logoBase64) {
        const logoWidth = 50;
        const logoHeight = 15;
        const logoX = (210 - logoWidth) / 2;
        doc.addImage(logoBase64, "PNG", logoX, 10, logoWidth, logoHeight);
      } else {
        doc.setFontSize(18);
        doc.setTextColor(9, 112, 45);
        doc.text("GoPass", 105, 20, { align: "center" });
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text("Portal de Recaudos", 105, 28, { align: "center" });
      }

      // Título principal
      doc.setFontSize(20);
      doc.setTextColor(9, 112, 45);
      doc.text("COMPROBANTE DE PAGO", 105, 40, { align: "center" });

      // Línea decorativa
      doc.setDrawColor(9, 112, 45);
      doc.setLineWidth(0.5);
      doc.line(15, 48, 195, 48);

      // Información de la empresa
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("Pagos Automáticos de Colombia", 105, 58, { align: "center" });

      // Fecha de generación
      doc.setFontSize(9);
      doc.text(`Fecha de emisión: ${new Date().toLocaleString("es-CO")}`, 15, 78);

      // Información de la transacción
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text("Detalles de la Transacción", 15, 92);

      // Crear tabla con los datos
      const tableData = [
        ["Número de Factura", receiptData.facturaNumero],
        ["Empresa / Usuario", receiptData.empresa],
        ["NIT / Identificación", receiptData.nit],
        ["Fecha y Hora", receiptData.fecha],
        ["ID de Transacción", receiptData.transactionId],
        ["Estado", receiptData.estado],
      ];

      if (receiptData.statusDetail) {
        tableData.push(["Detalle del Estado", receiptData.statusDetail]);
      }

      autoTable(doc, {
        startY: 100,
        head: [["Campo", "Valor"]],
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: [9, 112, 45],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 10,
        },
        bodyStyles: {
          fontSize: 9,
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        styles: { cellPadding: 5 },
        columnStyles: {
          0: { fontStyle: "bold", textColor: [0, 0, 0], cellWidth: 50 },
          1: { textColor: [0, 0, 0], cellWidth: 130 },
        },
        margin: { left: 15, right: 15 },
      });

      // Total
      const finalY = doc.lastAutoTable.finalY + 12;

      doc.setDrawColor(200, 200, 200);
      doc.line(15, finalY - 5, 195, finalY - 5);

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text("Total Pagado:", 120, finalY);

      doc.setFontSize(18);
      doc.setTextColor(9, 112, 45);
      doc.text(formatCOP(receiptData.monto), 120, finalY + 8);

      // Línea final
      doc.setDrawColor(9, 112, 45);
      doc.line(15, finalY + 18, 195, finalY + 18);

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text("Este documento es un comprobante de pago válido.", 105, finalY + 32, {
        align: "center",
      });
      doc.text(
        "Para cualquier inquietud, comuníquese con nuestro centro de atención.",
        105,
        finalY + 38,
        { align: "center" }
      );

      // Generar nombre del archivo
      const fileName = `comprobante_pago_${receiptData.transactionId.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}.pdf`;

      doc.save(fileName);

      if (onSuccess) {
        onSuccess("PDF descargado correctamente");
      }
    } catch (err) {
      console.error("Error generando PDF:", err);
      setError(`Error al generar el PDF: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById("receipt-content");
    if (!printContent) return;

    const originalContents = document.body.innerHTML;
    const printContents = printContent.innerHTML;

    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  if (!invoiceData) return null;

  const receiptData = getReceiptData(invoiceData);
  if (!receiptData) return null;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="modal-title">
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            background: facturaGradient,
            color: "#fff",
            mx: { xs: -2, sm: -3, md: -4 },
            mt: { xs: -2, sm: -3, md: -4 },
            px: { xs: 2, sm: 3, md: 4 },
            py: 2.5,
            mb: 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PdfIcon sx={{ color: "#fff", fontSize: 24 }} />
            <Typography
              id="modal-title"
              variant="h5"
              component="h2"
              fontWeight="bold"
              sx={{ color: "#fff", fontSize: 18 }}
            >
              Comprobante de Pago
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Cerrar comprobante"
            size="small"
            sx={{ color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Contenido del comprobante para imprimir */}
        <Box id="receipt-content" sx={{ p: 2 }}>
          <Box textAlign="center" mb={2}>
            <Box
              component="img"
              src={logoImage}
              alt="GoPass"
              sx={{
                height: { xs: 50, sm: 70 },
                width: "auto",
                objectFit: "contain",
                mb: 1,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Typography variant="subtitle1" color="text.secondary">
              Portal de Recaudos
            </Typography>
          </Box>

          <Paper sx={{ p: 3, bgcolor: colors.gray, borderRadius: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Número de Factura
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {receiptData.facturaNumero}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Monto Pagado
                </Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {formatCOP(receiptData.monto)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Empresa / Usuario
                </Typography>
                <Typography variant="body1">{receiptData.empresa}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  NIT / Identificación
                </Typography>
                <Typography variant="body1">{receiptData.nit}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Fecha y Hora
                </Typography>
                <Typography variant="body1">{receiptData.fecha}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  ID de Transacción
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {receiptData.transactionId}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Estado
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight="bold">
                  ✅ {receiptData.estado}
                </Typography>
              </Grid>
              {receiptData.statusDetail && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Detalle del Estado
                  </Typography>
                  <Typography variant="body2">{receiptData.statusDetail}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          <Divider sx={{ my: 2 }} />

          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Este comprobante certifica que el pago ha sido procesado exitosamente. Puede descargar
              este comprobante para sus registros.
            </Typography>
          </Alert>
        </Box>

        <Box display="flex" gap={2} justifyContent="flex-end" flexWrap="wrap" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            disabled={loading}
            sx={{
              borderColor: colors.primary,
              color: colors.primary,
              "&:hover": {
                borderColor: colors.secondary,
                backgroundColor: "rgba(9, 112, 45, 0.05)",
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : "Descargar PDF"}
          </Button>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              color: colors.white,
              bgcolor: colors.primary,
              "&:hover": { bgcolor: colors.secondary },
            }}
          >
            Cerrar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// PropTypes validation
PaymentReceiptModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invoiceData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    invoiceNumber: PropTypes.string,
    invoice_number: PropTypes.string,
    nit: PropTypes.string,
    company: PropTypes.string,
    amount: PropTypes.number,
    status: PropTypes.string,
    statusBadge: PropTypes.shape({
      text: PropTypes.string,
      color: PropTypes.string,
    }),
    statusDetail: PropTypes.string,
    paymentId: PropTypes.string,
    createdAtFormatted: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
};

PaymentReceiptModal.defaultProps = {
  invoiceData: null,
  onSuccess: null,
};

export default PaymentReceiptModal;
